/**
 * DAGCanvas — ReactFlow canvas with dagre auto-layout for the agent pipeline.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │  THIS IS THE PIPELINE CANVAS (workspace DAG).                       │
 * │  It shows TABLE-LEVEL nodes: source group → transforms → mapping    │
 * │  → pipeline output → target tables → destination.                   │
 * │                                                                     │
 * │  NOT the mapping canvas. The mapping canvas (field-level detail)    │
 * │  lives in ui/src/canvas/GraphCanvas.tsx and uses a completely       │
 * │  separate node type registry, graph builder, and layout engine.     │
 * │                                                                     │
 * │  See docs/CANVAS_ARCHITECTURE.md for the full distinction.          │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Direction: TB (top-to-bottom) matching the UX mock vertical flow.
 * Uses dagre for automatic layout, custom DAGNode type, and supports:
 *   - Phase hint overlay (building / executing / review_required)
 *   - Execution overlay (PipelineExecutionView)
 *   - Node selection callback
 *
 * Graph builder: pipelineNodeBuilder.ts (operationsToNodes)
 * Layout: dagre (automatic top-to-bottom)
 * Node types: dagNode, sourceGroupNode, mappingOutputNode, pipelineOutputNode,
 *             destinationNode, targetGroupNode, targetTableNode
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  ControlButton,
  MiniMap,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useUpdateNodeInternals,
  MarkerType,
} from 'reactflow';
import type { Node, Edge, NodeChange } from 'reactflow';
import { Position } from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Box, Button, Chip, Typography, alpha, useTheme } from '@mui/material';
import {
  Build as BuildIcon,
  PlayArrow as RunIcon,
  RateReview as ReviewIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import DAGNode from './DAGNode';
import type { DAGNodeData } from './DAGNode';
import SourceGroupNode from './SourceGroupNode';
import type { SourceGroupNodeData } from './SourceGroupNode';
import MappingOutputNode from './MappingOutputNode';
import type { MappingOutputNodeData } from './MappingOutputNode';
import PipelineOutputNode from './PipelineOutputNode';
import type { PipelineOutputNodeData } from './PipelineOutputNode';
import AssemblyNode from './AssemblyNode';
import type { AssemblyNodeData } from './AssemblyNode';
import DestinationNode from './DestinationNode';
import type { DestinationNodeData } from './DestinationNode';
import TargetGroupNode from './TargetGroupNode';
import type { TargetGroupNodeData } from './TargetGroupNode';
import TargetTableNode from './TargetTableNode';
import type { TargetTableNodeData } from './TargetTableNode';
import TargetSchemaContainerNode from './TargetSchemaContainerNode';
import type {
  TargetSchemaContainerNodeData,
  TargetSchemaTableEntry,
} from './TargetSchemaContainerNode';
import AddNodeFAB from './AddNodeFAB';
import type { AddNodeType } from './AddNodeFAB';
import type { PipelineCanvasLayout } from '../../types/canvasLayout';

// ── Types ────────────────────────────────────────────────────────────

export interface PipelineNode {
  stepId: string;
  stepType: string;
  title: string;
  /** Short user-facing operation type, displayed as a small pill above the title (e.g. "SQL", "Map", "LLM extract"). */
  kind?: string;
  description?: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'awaiting_review';
  rowCount?: number;
  columnCount?: number;
  cost?: number;
  reviewResult?: 'pass' | 'fixed' | 'warning' | null;
  progress?: number;
  inputRefs: string[];
  outputRef?: string;
  operationStatus?: string;
  /** Whether this is an exploration/validation step (rendered with muted style). */
  exploratory?: boolean;
  /** Truncated SQL/Python code snippet for display on the node. */
  codeSnippet?: string;
  /** Individual review checks from the review agent. */
  reviewChecks?: Array<{ name: string; passed: boolean; measured?: number; threshold?: number }>;
  /** Auto-corrections applied by the review agent. */
  reviewCorrections?: Array<{ check_name: string; fix_type: string; description: string; succeeded: boolean }>;
  /** Source table names contained in a source_group node (for edge construction). */
  _sourceTableNames?: string[];
  /** Source table details for SourceGroupNode rendering. */
  _sourceTables?: Array<{ name: string; displayName?: string; rowCount: number; columnCount: number }>;
  /** Connector type — for DestinationNode (e.g. "postgres", "s3") or per-origin source group icon. */
  _connectorType?: string | null;
  /** Destination target table/path for DestinationNode. */
  _targetTable?: string;
  /** Destination export status for DestinationNode. */
  _destinationStatus?: 'pending' | 'exporting' | 'exported' | 'failed';
  /** Destination ISO timestamp when export completed. */
  _destinationExportedAt?: string;
  /** Destination error message for DestinationNode. */
  _destinationError?: string;
  /** Batch ID for mapping portal navigation. */
  _batchId?: string;
  /** Current batch status for mapping node lifecycle. */
  _batchStatus?: string;
  /** Workspace mapping mode ('engine' | 'iterative') for mapping node UI. */
  _mappingMode?: string;
  /** Target table entries for TargetGroupNode rendering. */
  _targetTables?: Array<{ name: string; viewName: string; status: string; ddl?: string; error?: string }>;
  /** Callback when a table in the target group is clicked. */
  _onTableClick?: (tableName: string) => void;
  /** Callback when a table in the source group is clicked for preview. */
  _onSourceTableClick?: (tableName: string) => void;
  /** Per-origin label when source groups are split by connector/origin (e.g. "File Uploads", "CRO Database"). */
  _originLabel?: string;
  /** Number of failed mapping views (for Pipeline Output warning badge). */
  _failedViewCount?: number;
  /** Export validation status for Pipeline Output badge. */
  _validationStatus?: 'pass' | 'warn' | 'error';
  /** Number of validation issues for Pipeline Output badge. */
  _validationCount?: number;
  /** Quality gate status from review aggregation. */
  _qualityStatus?: 'pass' | 'warn' | 'fail' | 'no_review';
  /** Number of quality issues. */
  _qualityIssueCount?: number;
  /** Number of health issues for target table nodes (from mapping health API). */
  _healthIssueCount?: number;
  /** Worst severity among health issues for this target table. */
  _healthSeverity?: 'error' | 'warning' | null;
  /** Operation id of the schema_map op backing this target_table node, if any.
   * Allows the inspector to surface the recipe / SQL / re-materialize action
   * for target_tables produced via the iterative mapping path. */
  _schemaMapOperationId?: string;
  /** Output format badge for hierarchical_assembly nodes (e.g. "xml", "json"). */
  _assemblyOutputFormat?: string;
  /** Output byte size for hierarchical_assembly nodes (from step metrics). */
  _assemblyByteSize?: number;
  /** Error string for failed hierarchical_assembly steps. */
  _assemblyError?: string;
  /** Inline download callback for hierarchical_assembly node header. */
  _onAssemblyDownload?: () => void;
  /** Additional outputRefs claimed by container-style nodes (e.g.
   * target_schema_container owns every __mapped_X__ view it encapsulates).
   * Used by DAGCanvas to register every owned ref in the outputToNode map so
   * downstream edges resolve to the container instead of dangling. */
  _additionalOutputRefs?: string[];
}

interface DAGCanvasProps {
  pipelineNodes: PipelineNode[];
  selectedNodeId: string | null;
  onNodeClick: (nodeId: string) => void;
  onPaneClick?: () => void;
  phase: 'building' | 'executing' | 'review_required';
  phaseDetail?: string;
  executionOverlay?: React.ReactNode;
  onDiscardNode?: (stepId: string) => void;
  onMarkExploratory?: (stepId: string, exploratory: boolean) => void;
  onRenameNode?: (stepId: string, label: string) => void;
  /** Complete outputRef→inputRefs map (including hidden/exploratory ops) for edge resolution. */
  allOutputToInputRefs?: Map<string, string[]>;
  /** Navigation callback for portal nodes (mapping_output). */
  onNavigate?: (path: string) => void;
  /** Callback to open the mapping editor in-place (mode switch). */
  onOpenEditor?: () => void;
  /** Callback to open the outputs drawer (replaces single-table download). */
  onViewAllOutputs?: () => void;
  /** When false, the persistent Outputs button stays disabled (e.g. mapping not done). */
  outputsEnabled?: boolean;
  /** Number of completed output tables (shown on pipeline output node). */
  outputCount?: number;
  /** Export callback for pipeline_output node (opens connector picker). */
  onExport?: () => void;
  /** Callback to add a post-mapping transform from the Pipeline Output node. */
  onAddTransform?: () => void;
  /** Callback when user clicks "+" to add a transform node. */
  onAddNode?: (type: AddNodeType) => void;
  /** Whether the agent is busy (disables FAB). */
  isBusy?: boolean;
  /** Whether a placeholder node already exists (disables FAB). */
  hasPendingNode?: boolean;
  /** Callback to open schema picker from MappingOutputNode. */
  onChangeSchema?: () => void;
  /** Callback to start export from DestinationNode. */
  onStartExport?: () => void;
  /** Callback to add a new source table (opens file picker). */
  onAddSource?: () => void;
  /** Callback to replace a specific source table (opens file picker). */
  onReplaceSource?: (tableName: string) => void;
  /** Callback to add source from a connector (opens connector picker). */
  onAddFromConnector?: () => void;
  /** Schema evolution warnings to highlight on DAG nodes. */
  schemaWarnings?: Map<string, import('./DAGNode').SchemaWarning>;
  /** When set, smoothly pans the canvas to center on this node ID. */
  focusNodeId?: string | null;
  /** Called after the focus pan completes so parent can reset the value. */
  onFocusComplete?: () => void;
  /** Right-click on empty canvas space (for context menu). */
  onPaneContextMenu?: (e: React.MouseEvent) => void;
  /** Per-target-table readiness, keyed by lowercase table name. When
   *  provided, each TargetTableNode renders a Readiness chip in
   *  preference to the legacy health chip. Parent (UnifiedCanvasLayout)
   *  computes this from a single /readiness?consumer_kind=target_table
   *  fetch so all per-node chips stay in sync. */
  readinessByTable?: Map<string, { status: import('../../types/readiness').ReadinessStatus; count: number }>;
  /** Per-target-table progress state from the unified progress channel,
   *  keyed by lowercase table name. When populated, each TargetTableNode
   *  renders a small state badge that reflects the live mapping run
   *  (queued / running / done / needs_input / failed). Parent feeds this
   *  from ``stream.progress.phases[mapping].tasks``. */
  progressByTable?: Map<string, import('../../types/progress').ProgressTask>;
  /** Persisted layout from workspace_meta.canvas_layout. */
  savedCanvasLayout?: PipelineCanvasLayout;
  /** Debounced by the canvas on drag end; immediate on layout-button click. */
  onCanvasLayoutChange?: (layout: PipelineCanvasLayout) => void;
}

// ── Constants ────────────────────────────────────────────────────────

const NODE_WIDTH = 220;
const NODE_HEIGHT = 110;
const SOURCE_GROUP_WIDTH = 260;
const SOURCE_GROUP_HEIGHT = 140;
const MAPPING_OUTPUT_WIDTH = 240;
const MAPPING_OUTPUT_HEIGHT = 100;
const PIPELINE_OUTPUT_WIDTH = 240;
const PIPELINE_OUTPUT_HEIGHT = 120;
const DESTINATION_WIDTH = 240;
const DESTINATION_HEIGHT = 100;
const TARGET_GROUP_WIDTH = 260;
const TARGET_GROUP_HEIGHT = 160;
const TARGET_TABLE_WIDTH = 180;
const TARGET_TABLE_HEIGHT = 56;
const ASSEMBLY_WIDTH = 240;
const ASSEMBLY_HEIGHT = 120;
const TARGET_SCHEMA_CONTAINER_WIDTH = 320;
// Fixed chrome (header + summary + actions row + borders). The body adds
// per-row height on top, capped at BODY_MAX_HEIGHT for the scroll viewport.
// Kept in sync with TargetSchemaContainerNode's layout so dagre reserves the
// right vertical space and the expanded list never overlaps neighbours.
const TARGET_SCHEMA_CHROME_HEIGHT = 140;
const TARGET_SCHEMA_ROW_HEIGHT = 22;
const TARGET_SCHEMA_BODY_MAX = 220;
function targetSchemaContainerHeight(tableCount: number): number {
  if (tableCount === 0) return TARGET_SCHEMA_CHROME_HEIGHT;
  const bodyHeight = Math.min(
    tableCount * TARGET_SCHEMA_ROW_HEIGHT + 12,
    TARGET_SCHEMA_BODY_MAX,
  );
  return TARGET_SCHEMA_CHROME_HEIGHT + bodyHeight;
}

const nodeTypes = {
  dagNode: DAGNode,
  sourceGroupNode: SourceGroupNode,
  mappingOutputNode: MappingOutputNode,
  pipelineOutputNode: PipelineOutputNode,
  destinationNode: DestinationNode,
  targetGroupNode: TargetGroupNode,
  targetTableNode: TargetTableNode,
  targetSchemaContainerNode: TargetSchemaContainerNode,
  assemblyNode: AssemblyNode,
};

const PHASE_CONFIG: Record<string, { label: string; color: string; icon: React.ReactElement }> = {
  building: { label: 'Building Pipeline', color: '#3b82f6', icon: <BuildIcon sx={{ fontSize: 14 }} /> },
  executing: { label: 'Executing', color: '#059669', icon: <RunIcon sx={{ fontSize: 14 }} /> },
  review_required: { label: 'Review Required', color: '#f59e0b', icon: <ReviewIcon sx={{ fontSize: 14 }} /> },
};

// ── Dagre layout ─────────────────────────────────────────────────────

/**
 * @param pipelineNodes  Visible nodes (already filtered by exploratory etc.)
 * @param allOutputToInputRefs  Complete outputRef→inputRefs map from ALL
 *        operations (including hidden/exploratory). Used to resolve edges
 *        through hidden intermediate nodes so that downstream visible nodes
 *        don't appear as dangling.
 */
export type LayoutDirection = 'TB' | 'LR';

function layoutDAG(
  pipelineNodes: PipelineNode[],
  allOutputToInputRefs?: Map<string, string[]>,
  direction: LayoutDirection = 'TB',
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep: 60, ranksep: 80 });
  const sourcePos = direction === 'LR' ? Position.Right : Position.Bottom;
  const targetPos = direction === 'LR' ? Position.Left : Position.Top;

  // Determine dagre node dimensions based on step type
  pipelineNodes.forEach((pn) => {
    if (pn.stepType === 'source_group') {
      g.setNode(pn.stepId, { width: SOURCE_GROUP_WIDTH, height: SOURCE_GROUP_HEIGHT });
    } else if (pn.stepType === 'mapping_output' || pn.stepType === 'schema_map') {
      g.setNode(pn.stepId, { width: MAPPING_OUTPUT_WIDTH, height: MAPPING_OUTPUT_HEIGHT });
    } else if (pn.stepType === 'pipeline_output') {
      g.setNode(pn.stepId, { width: PIPELINE_OUTPUT_WIDTH, height: PIPELINE_OUTPUT_HEIGHT });
    } else if (pn.stepType === 'destination') {
      g.setNode(pn.stepId, { width: DESTINATION_WIDTH, height: DESTINATION_HEIGHT });
    } else if (pn.stepType === 'target_group') {
      g.setNode(pn.stepId, { width: TARGET_GROUP_WIDTH, height: TARGET_GROUP_HEIGHT });
    } else if (pn.stepType === 'target_table') {
      g.setNode(pn.stepId, { width: TARGET_TABLE_WIDTH, height: TARGET_TABLE_HEIGHT });
    } else if (pn.stepType === 'target_schema_container') {
      const tableCount = (pn._targetTables?.length) ?? 0;
      g.setNode(pn.stepId, {
        width: TARGET_SCHEMA_CONTAINER_WIDTH,
        height: targetSchemaContainerHeight(tableCount),
      });
    } else if (pn.stepType === 'hierarchical_assembly') {
      g.setNode(pn.stepId, { width: ASSEMBLY_WIDTH, height: ASSEMBLY_HEIGHT });
    } else {
      g.setNode(pn.stepId, { width: NODE_WIDTH, height: NODE_HEIGHT });
    }
  });

  // Map each source table name → its owning source group node ID.
  // When multiple origin-based source groups exist, each table resolves
  // to the correct per-origin source group node instead of a single node.
  const sourceTableToGroup = new Map<string, string>();
  pipelineNodes.forEach((pn) => {
    if (pn.stepType === 'source_group' && pn._sourceTableNames) {
      pn._sourceTableNames.forEach((name) => sourceTableToGroup.set(name, pn.stepId));
    }
  });

  // Build outputRef → nodeId map for edge construction.
  // Every visible node with an outputRef participates. Earlier code excluded
  // target_table / target_group as a workaround for the schema_map collision
  // on __mapped_X__; now the builder suppresses redundant target_table nodes
  // when a real schema_map op produces the same view, so no exclusion is
  // needed and post-map transforms can resolve directly to target tables.
  const outputToNode = new Map<string, string>();
  pipelineNodes.forEach((pn) => {
    if (pn.outputRef) {
      outputToNode.set(pn.outputRef, pn.stepId);
    }
    // Container-style nodes (currently target_schema_container) own multiple
    // logical outputs — every __mapped_X__ view they encapsulate. Register
    // each so downstream consumers (assembly, post-map transforms) resolve
    // their edges to the container instead of dangling.
    if (pn._additionalOutputRefs) {
      for (const ref of pn._additionalOutputRefs) {
        if (!outputToNode.has(ref)) outputToNode.set(ref, pn.stepId);
      }
    }
  });

  // Helper: resolve an inputRef to all visible producer node IDs by walking
  // through hidden (e.g. exploratory) intermediate operations. Returns the
  // owning source group node ID if the ref traces to a source table, an
  // empty array if unresolvable. For hidden ops with multiple parents
  // (e.g. fuzzy_match with two inputs), returns ALL resolvable parents so
  // the visible downstream node can show every true source.
  function resolveAllVisible(ref: string, visited?: Set<string>): string[] {
    const owningGroup = sourceTableToGroup.get(ref);
    if (owningGroup) return [owningGroup];
    const nodeId = outputToNode.get(ref);
    if (nodeId) return [nodeId];
    if (!allOutputToInputRefs) return [];
    const parentInputs = allOutputToInputRefs.get(ref);
    if (!parentInputs || parentInputs.length === 0) return [];
    const seen = visited ?? new Set<string>();
    if (seen.has(ref)) return [];
    seen.add(ref);
    const out: string[] = [];
    for (const parentRef of parentInputs) {
      for (const resolved of resolveAllVisible(parentRef, seen)) {
        if (!out.includes(resolved)) out.push(resolved);
      }
    }
    return out;
  }

  // Arrow marker applied to every edge so flow direction is always clear
  const arrowMarker = (color: string) => ({
    type: MarkerType.ArrowClosed,
    width: 14,
    height: 14,
    color,
  });

  const edges: Edge[] = [];
  const edgeSet = new Set<string>(); // Deduplicate

  pipelineNodes.forEach((pn) => {
    // Skip source group nodes (no inputs)
    if (pn.stepType === 'source_group') return;

    const isExploratory = pn.exploratory === true;

    pn.inputRefs.forEach((ref) => {
      // Resolve the inputRef to ALL visible producers (handles hidden
      // multi-input ops like fuzzy_match correctly). Each producer becomes
      // its own edge so visible downstream nodes show every true source.
      const resolvedSourceIds = resolveAllVisible(ref);
      if (resolvedSourceIds.length === 0 && pn.stepType !== 'source_group') {
        // Surface unresolved refs as a console warning so genuine bugs in
        // the builder surface instead of silently producing orphans.
        // eslint-disable-next-line no-console
        console.warn('[DAGCanvas] Unresolved inputRef', { stepId: pn.stepId, stepType: pn.stepType, ref });
      }
      for (const resolvedSourceId of resolvedSourceIds) {
        if (resolvedSourceId === pn.stepId) continue;
        const edgeId = `${resolvedSourceId}->${pn.stepId}`;
        if (edgeSet.has(edgeId)) continue;
        edgeSet.add(edgeId);
        const strokeColor = pn.status === 'running' ? '#3b82f6' : isExploratory ? '#cbd5e1' : '#94a3b8';
        edges.push({
          id: edgeId,
          source: resolvedSourceId,
          target: pn.stepId,
          animated: pn.status === 'running',
          markerEnd: arrowMarker(strokeColor),
          style: {
            stroke: strokeColor,
            strokeWidth: isExploratory ? 1.5 : 2,
            strokeDasharray: isExploratory ? '6 3' : undefined,
          },
        });
        g.setEdge(resolvedSourceId, pn.stepId);
      }
    });
  });

  // Connect dangling source group nodes to the same downstream target as
  // connected source groups. This handles connector sources that were part of
  // the pipeline but couldn't be loaded into DuckDB (so no operations reference them).
  // Purely topology-based — works regardless of downstream node type.
  const sourceGroupIds = new Set(
    pipelineNodes.filter((pn) => pn.stepType === 'source_group').map((pn) => pn.stepId),
  );
  const connectedSources = new Set(
    edges.filter((e) => sourceGroupIds.has(e.source)).map((e) => e.source),
  );
  const danglingSourceGroups = Array.from(sourceGroupIds).filter((id) => !connectedSources.has(id));
  if (danglingSourceGroups.length > 0 && connectedSources.size > 0) {
    // Use the same target that connected source groups feed into
    const targetId = edges.find((e) => connectedSources.has(e.source))?.target;
    if (targetId) {
      for (const srcId of danglingSourceGroups) {
        const edgeId = `${srcId}->${targetId}`;
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: srcId,
            target: targetId,
            animated: false,
            markerEnd: arrowMarker('#94a3b8'),
            style: { stroke: '#94a3b8', strokeWidth: 2, strokeDasharray: '6 3' },
          });
          g.setEdge(srcId, targetId);
        }
      }
    }
  }

  // (No sequential fallback — chaining nodes by array order masks genuine
  // edge-resolution bugs as if they were correct topology. If a non-source
  // node has no incoming edges, it appears as a true orphan and the
  // resolveAllVisible warning above tells us why.)

  dagre.layout(g);

  const nodes: Node[] = pipelineNodes.map((pn) => {
    const pos = g.node(pn.stepId);

    // Determine node type and dimensions
    if (pn.stepType === 'source_group') {
      return {
        id: pn.stepId,
        type: 'sourceGroupNode',
        position: {
          x: pos.x - SOURCE_GROUP_WIDTH / 2,
          y: pos.y - SOURCE_GROUP_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          tableCount: pn._sourceTables?.length || 0,
          totalRowCount: pn._sourceTables?.reduce((s, t) => s + t.rowCount, 0) || 0,
          tables: pn._sourceTables || [],
          status: pn.status,
          onTableClick: (pn as any)._onSourceTableClick,
          originLabel: (pn as any)._originLabel,
          connectorType: (pn as any)._connectorType,
        } as SourceGroupNodeData,
      };
    }

    if (pn.stepType === 'mapping_output' || pn.stepType === 'schema_map') {
      return {
        id: pn.stepId,
        type: 'mappingOutputNode',
        position: {
          x: pos.x - MAPPING_OUTPUT_WIDTH / 2,
          y: pos.y - MAPPING_OUTPUT_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          title: pn.title,
          description: pn.description || '',
          status: pn.status,
          rowCount: pn.rowCount,
          columnCount: pn.columnCount,
          batchId: pn._batchId,
          batchStatus: pn._batchStatus,
        } as MappingOutputNodeData,
      };
    }

    if (pn.stepType === 'pipeline_output') {
      return {
        id: pn.stepId,
        type: 'pipelineOutputNode',
        position: {
          x: pos.x - PIPELINE_OUTPUT_WIDTH / 2,
          y: pos.y - PIPELINE_OUTPUT_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          title: pn.title,
          description: pn.description,
          status: pn.status,
          rowCount: pn.rowCount,
          columnCount: pn.columnCount,
          failedViewCount: pn._failedViewCount,
          validationStatus: pn._validationStatus,
          validationCount: pn._validationCount,
          qualityStatus: pn._qualityStatus,
          qualityIssueCount: pn._qualityIssueCount,
        } as PipelineOutputNodeData,
      };
    }

    if (pn.stepType === 'destination') {
      return {
        id: pn.stepId,
        type: 'destinationNode',
        position: {
          x: pos.x - DESTINATION_WIDTH / 2,
          y: pos.y - DESTINATION_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          title: pn.title,
          connectorType: (pn as any)._connectorType || '',
          targetTable: (pn as any)._targetTable,
          status: (pn as any)._destinationStatus || 'pending',
          rowsWritten: pn.rowCount,
          exportedAt: (pn as any)._destinationExportedAt,
          error: (pn as any)._destinationError,
        } as DestinationNodeData,
      };
    }

    if (pn.stepType === 'target_group') {
      return {
        id: pn.stepId,
        type: 'targetGroupNode',
        position: {
          x: pos.x - TARGET_GROUP_WIDTH / 2,
          y: pos.y - TARGET_GROUP_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          tableCount: (pn as any)._targetTables?.length || 0,
          tables: (pn as any)._targetTables || [],
          onTableClick: (pn as any)._onTableClick,
        } as TargetGroupNodeData,
      };
    }

    if (pn.stepType === 'hierarchical_assembly') {
      const assemblyStatus: AssemblyNodeData['status'] =
        pn.status === 'awaiting_review' ? 'pending' : (pn.status as AssemblyNodeData['status']);
      return {
        id: pn.stepId,
        type: 'assemblyNode',
        position: {
          x: pos.x - ASSEMBLY_WIDTH / 2,
          y: pos.y - ASSEMBLY_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          title: pn.title,
          status: assemblyStatus,
          outputFormat: pn._assemblyOutputFormat,
          rowCount: pn.rowCount,
          byteSize: pn._assemblyByteSize,
          error: pn._assemblyError,
          onDownload: (pn as any)._onAssemblyDownload,
        } as AssemblyNodeData,
      };
    }

    if (pn.stepType === 'target_table') {
      return {
        id: pn.stepId,
        type: 'targetTableNode',
        position: {
          x: pos.x - TARGET_TABLE_WIDTH / 2,
          y: pos.y - TARGET_TABLE_HEIGHT / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          tableName: pn.title,
          status: pn.status,
          description: pn.description,
          error: pn.status === 'failed' ? pn.description?.replace('Error: ', '') : undefined,
          healthIssueCount: pn._healthIssueCount,
          healthSeverity: pn._healthSeverity,
        } as TargetTableNodeData,
      };
    }

    if (pn.stepType === 'target_schema_container') {
      const tableCount = (pn._targetTables?.length) ?? 0;
      const containerHeight = targetSchemaContainerHeight(tableCount);
      return {
        id: pn.stepId,
        type: 'targetSchemaContainerNode',
        position: {
          x: pos.x - TARGET_SCHEMA_CONTAINER_WIDTH / 2,
          y: pos.y - containerHeight / 2,
        },
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        data: {
          title: pn.title,
          description: pn.description ?? '',
          status: pn.status,
          batchStatus: pn._batchStatus,
          mappingMode: pn._mappingMode,
          tables: ((pn._targetTables ?? []) as TargetSchemaTableEntry[]),
          onTableClick: pn._onTableClick,
          // onOpenEditor / onChangeSchema are injected later by
          // nodesWithCallbacks (same pattern as MappingOutputNode used).
        } as TargetSchemaContainerNodeData,
      };
    }

    // Default: DAGNode
    return {
      id: pn.stepId,
      type: 'dagNode',
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
      sourcePosition: sourcePos,
      targetPosition: targetPos,
      data: {
        stepId: pn.stepId,
        stepType: pn.stepType,
        title: pn.title,
        kind: pn.kind,
        description: pn.description,
        status: pn.status,
        rowCount: pn.rowCount,
        columnCount: pn.columnCount,
        cost: pn.cost,
        reviewResult: pn.reviewResult,
        progress: pn.progress,
        inputRefs: pn.inputRefs,
        outputRef: pn.outputRef,
        operationStatus: pn.operationStatus,
        exploratory: pn.exploratory,
        codeSnippet: pn.codeSnippet,
        reviewChecks: pn.reviewChecks,
        reviewCorrections: pn.reviewCorrections,
      } as DAGNodeData,
    };
  });

  return { nodes, edges };
}

// ── Inner canvas (needs ReactFlowProvider) ───────────────────────────

const DAGCanvasInner: React.FC<DAGCanvasProps> = ({
  pipelineNodes,
  selectedNodeId,
  onNodeClick,
  onPaneClick,
  phase,
  phaseDetail,
  executionOverlay,
  onDiscardNode,
  onMarkExploratory,
  onRenameNode,
  allOutputToInputRefs,
  onNavigate,
  onOpenEditor,
  onViewAllOutputs,
  outputsEnabled = false,
  outputCount,
  onExport,
  onAddTransform,
  onAddNode,
  isBusy,
  hasPendingNode,
  onChangeSchema,
  onStartExport,
  onAddSource,
  onReplaceSource,
  onAddFromConnector,
  schemaWarnings,
  focusNodeId,
  onFocusComplete,
  onPaneContextMenu,
  readinessByTable,
  progressByTable,
  savedCanvasLayout,
  onCanvasLayoutChange,
}) => {
  const theme = useTheme();
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const [viewportApplied, setViewportApplied] = useState(false);
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>(
    () => savedCanvasLayout?.direction ?? 'TB',
  );
  // Bumped on every layout-button click so identical direction clicks still
  // trigger a re-layout (useful for resetting after manual node drags).
  const [relayoutTick, setRelayoutTick] = useState(0);
  const prevDirectionRef = useRef<LayoutDirection>(savedCanvasLayout?.direction ?? 'TB');
  const prevRelayoutTickRef = useRef(relayoutTick);
  // Positions the user dragged manually — cleared only on explicit TB/LR layout.
  const manualPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(Object.entries(savedCanvasLayout?.node_positions ?? {})),
  );
  const hasHydratedFromMetaRef = useRef(false);
  const localLayoutDirtyRef = useRef(false);
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const layoutDirectionRef = useRef(layoutDirection);
  layoutDirectionRef.current = layoutDirection;
  // Bumped once when workspace_meta layout hydrates so saved positions apply
  // even when direction matches the default (TB).
  const [hydrationTick, setHydrationTick] = useState(0);

  const buildCanvasLayout = useCallback((): PipelineCanvasLayout => {
    const node_positions: Record<string, { x: number; y: number }> = {};
    manualPositionsRef.current.forEach((pos, id) => {
      node_positions[id] = pos;
    });
    return { direction: layoutDirectionRef.current, node_positions };
  }, []);

  const persistCanvasLayout = useCallback(() => {
    onCanvasLayoutChange?.(buildCanvasLayout());
  }, [onCanvasLayoutChange, buildCanvasLayout]);

  const schedulePersistCanvasLayout = useCallback(() => {
    if (!onCanvasLayoutChange) return;
    if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    persistTimeoutRef.current = setTimeout(() => {
      persistTimeoutRef.current = null;
      persistCanvasLayout();
    }, 300);
  }, [onCanvasLayoutChange, persistCanvasLayout]);

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    };
  }, []);

  // Hydrate once when workspace_meta arrives after the session query resolves.
  // Never overwrite positions the user already moved locally (meta fetch can
  // complete after drag, or return stale empty node_positions).
  useEffect(() => {
    if (!savedCanvasLayout || hasHydratedFromMetaRef.current) return;
    hasHydratedFromMetaRef.current = true;

    prevDirectionRef.current = savedCanvasLayout.direction;
    setLayoutDirection(savedCanvasLayout.direction);

    if (!localLayoutDirtyRef.current) {
      const entries = Object.entries(savedCanvasLayout.node_positions ?? {});
      if (entries.length > 0) {
        manualPositionsRef.current = new Map(entries);
        setHydrationTick((t) => t + 1);
      }
    }
  }, [savedCanvasLayout]);

  // Layout nodes + edges
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(
    () => layoutDAG(pipelineNodes, allOutputToInputRefs, layoutDirection),
    [pipelineNodes, allOutputToInputRefs, layoutDirection],
  );

  // Inject action callbacks into node data
  const nodesWithCallbacks = useMemo(
    () =>
      layoutedNodes.map((node) => {
        if (node.type === 'dagNode') {
          return {
            ...node,
            data: {
              ...node.data,
              onDiscard: onDiscardNode,
              onMarkExploratory: onMarkExploratory,
              onRename: onRenameNode,
              schemaWarning: schemaWarnings?.get(node.id),
            },
          };
        }
        if (node.type === 'mappingOutputNode') {
          return {
            ...node,
            data: {
              ...node.data,
              ...(onNavigate && { onNavigate }),
              ...(onOpenEditor && { onOpenEditor }),
              ...(onChangeSchema && { onChangeSchema }),
            },
          };
        }
        if (node.type === 'targetSchemaContainerNode') {
          return {
            ...node,
            data: {
              ...node.data,
              ...(onOpenEditor && { onOpenEditor }),
              ...(onChangeSchema && { onChangeSchema }),
            },
          };
        }
        if (node.type === 'pipelineOutputNode') {
          return {
            ...node,
            data: {
              ...node.data,
              outputCount,
              onViewAllOutputs,
              onExport,
              onAddTransform,
            },
          };
        }
        if (node.type === 'sourceGroupNode') {
          return {
            ...node,
            data: {
              ...node.data,
              ...(onAddSource && { onAddSource }),
              ...(onReplaceSource && { onReplaceTable: onReplaceSource }),
              ...(onAddFromConnector && { onAddFromConnector }),
            },
          };
        }
        if (node.type === 'destinationNode' && onStartExport) {
          return {
            ...node,
            data: {
              ...node.data,
              onStartExport,
            },
          };
        }
        if (node.type === 'targetTableNode' && (readinessByTable || progressByTable)) {
          // Lowercase the table name for the lookup so case-skew
          // between the recipe (often snake_case) and the
          // /readiness response (whichever the finding emitted)
          // doesn't silently miss the match.
          const name = String((node.data as TargetTableNodeData).tableName || '').toLowerCase();
          const r = readinessByTable?.get(name);
          // Progress task lookup uses the raw table id; the reducer
          // doesn't lowercase, so do an exact match first and fall
          // back to lowercase for tolerance.
          const rawName = String((node.data as TargetTableNodeData).tableName || '');
          const pTask = progressByTable?.get(rawName) || progressByTable?.get(name);
          const patches: Partial<TargetTableNodeData> = {};
          if (r) {
            patches.readinessStatus = r.status;
            patches.readinessFindingCount = r.count;
          }
          if (pTask) {
            patches.progressState = pTask.state;
            patches.progressActivity = pTask.activity || pTask.reason || null;
          }
          if (Object.keys(patches).length > 0) {
            return { ...node, data: { ...node.data, ...patches } };
          }
        }
        return node;
      }),
    [layoutedNodes, onDiscardNode, onMarkExploratory, onRenameNode, onNavigate, onOpenEditor, onViewAllOutputs, outputCount, onExport, onAddTransform, onChangeSchema, onStartExport, onAddSource, onReplaceSource, onAddFromConnector, schemaWarnings, readinessByTable, progressByTable],
  );

  const [nodes, setNodes, onNodesChangeBase] = useNodesState(nodesWithCallbacks);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  const snapshotAllNodePositions = useCallback(() => {
    reactFlowInstance.getNodes().forEach((n) => {
      manualPositionsRef.current.set(n.id, { x: n.position.x, y: n.position.y });
    });
  }, [reactFlowInstance]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      let dragEnded = false;
      for (const change of changes) {
        if (change.type === 'position' && change.dragging === false) {
          dragEnded = true;
          if (change.position) {
            manualPositionsRef.current.set(change.id, change.position);
          }
        }
      }
      onNodesChangeBase(changes);
      if (dragEnded) {
        localLayoutDirtyRef.current = true;
        // Snapshot every node so the whole canvas stays put across polls,
        // not just the node that was dragged.
        requestAnimationFrame(() => {
          snapshotAllNodePositions();
          schedulePersistCanvasLayout();
        });
      }
    },
    [onNodesChangeBase, schedulePersistCanvasLayout, snapshotAllNodePositions],
  );

  // Track structural identity of the layout — node IDs PLUS any per-node
  // dimension that materially affects dagre's spacing. The container's
  // table count is the main one: when an engine-mode batch completes
  // (0 → 24 tables), the container's allocated height jumps from ~140 to
  // ~360, and neighbours need re-layout or they'll overlap the now-tall
  // container.
  const layoutStructureKey = useMemo(
    () =>
      pipelineNodes
        .map((pn) =>
          pn.stepType === 'target_schema_container'
            ? `${pn.stepId}#${pn._targetTables?.length ?? 0}`
            : pn.stepId,
        )
        .join(','),
    [pipelineNodes],
  );

  // Sync layout when pipelineNodes change.
  // Manual positions (user drag) are preserved across data polling and topology
  // changes. Only explicit TB/LR layout buttons reset to dagre auto-layout.
  const prevStructureKeyRef = useRef(layoutStructureKey);
  useEffect(() => {
    const relayoutRequested = prevRelayoutTickRef.current !== relayoutTick;
    prevDirectionRef.current = layoutDirection;
    prevRelayoutTickRef.current = relayoutTick;
    prevStructureKeyRef.current = layoutStructureKey;

    // Only explicit TB/LR button clicks bump relayoutTick — not direction
    // hydration from workspace_meta (which would wipe saved positions).
    const explicitLayoutReset = relayoutRequested;

    setNodes((currentNodes) => {
      const currentPosById = new Map(currentNodes.map((n) => [n.id, n.position]));
      const newIds = new Set(nodesWithCallbacks.map((n) => n.id));

      manualPositionsRef.current.forEach((_, id) => {
        if (!newIds.has(id)) {
          manualPositionsRef.current.delete(id);
        }
      });

      if (explicitLayoutReset) {
        return nodesWithCallbacks;
      }

      return nodesWithCallbacks.map((dagreNode) => {
        const manual = manualPositionsRef.current.get(dagreNode.id);
        if (manual) {
          return { ...dagreNode, position: manual };
        }
        const current = currentPosById.get(dagreNode.id);
        if (current) {
          return { ...dagreNode, position: current };
        }
        return dagreNode;
      });
    });
    setEdges(layoutedEdges);
  }, [nodesWithCallbacks, layoutedEdges, setNodes, setEdges, layoutDirection, relayoutTick, layoutStructureKey, hydrationTick]);

  // Only reset fitView when the actual node topology changes (new/removed nodes)
  useEffect(() => {
    if (manualPositionsRef.current.size === 0) {
      setViewportApplied(false);
    }
  }, [layoutStructureKey, layoutDirection, relayoutTick]);

  // workspace_meta may arrive after the first fitView — refit once saved positions apply.
  useEffect(() => {
    if (hydrationTick > 0 && manualPositionsRef.current.size > 0) {
      setViewportApplied(false);
    }
  }, [hydrationTick]);

  // When layout direction toggles, each node component swaps its Handle's
  // `position` prop. React Flow caches handleBounds at mount time and won't
  // re-measure on a prop change alone, so edges keep terminating at the OLD
  // handle coordinates (visible as edges hitting the top-left corner instead
  // of left-center after switching to LR). Force a per-node re-measure so
  // handle positions track the swap.
  useEffect(() => {
    layoutedNodes.forEach((n) => updateNodeInternals(n.id));
  }, [layoutDirection, relayoutTick, layoutedNodes, updateNodeInternals]);

  const applyLayout = useCallback((dir: LayoutDirection) => {
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
      persistTimeoutRef.current = null;
    }
    manualPositionsRef.current.clear();
    localLayoutDirtyRef.current = false;
    setLayoutDirection(dir);
    setRelayoutTick((t) => t + 1);
    onCanvasLayoutChange?.({ direction: dir, node_positions: {} });
  }, [onCanvasLayoutChange]);

  // Fit view after nodes are set. Saved node positions are preserved; only zoom/pan adjust.
  useEffect(() => {
    if (viewportApplied || nodes.length === 0 || !reactFlowInstance) return;

    let timer1: ReturnType<typeof setTimeout> | undefined;
    let timer2: ReturnType<typeof setTimeout> | undefined;
    const raf = requestAnimationFrame(() => {
      timer1 = setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2, duration: 300 });
        setViewportApplied(true);
        // Second pass after React Flow settles — avoids stale bounds on hydrated layouts.
        timer2 = setTimeout(() => {
          reactFlowInstance.fitView({ padding: 0.2, duration: 250 });
        }, 300);
      }, 100);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
    };
  }, [viewportApplied, nodes, reactFlowInstance, hydrationTick]);

  // Pan to a specific node when focusNodeId is set (e.g., from clicking a tool_result bar).
  // Per-table target ids (__target_<name>__) no longer exist as standalone
  // nodes — they're folded into the target_schema_container. Fall back to
  // focusing the container in that case.
  useEffect(() => {
    if (!focusNodeId || !reactFlowInstance) return;
    let node = reactFlowInstance.getNode(focusNodeId);
    if (!node && focusNodeId.startsWith('__target_') && focusNodeId !== '__target_schema__') {
      node = reactFlowInstance.getNode('__target_schema__');
    }
    if (!node) { onFocusComplete?.(); return; }
    const x = node.position.x + (node.width ?? NODE_WIDTH) / 2;
    const y = node.position.y + (node.height ?? NODE_HEIGHT) / 2;
    reactFlowInstance.setCenter(x, y, { zoom: 1.2, duration: 400 });
    onFocusComplete?.();
  }, [focusNodeId, reactFlowInstance, onFocusComplete]);

  // Apply selection styling
  const styledNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
      })),
    [nodes, selectedNodeId],
  );

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeClick(node.id);
    },
    [onNodeClick],
  );

  const phaseConfig = PHASE_CONFIG[phase];

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        borderRadius: 2,
        overflow: 'hidden',
        ...(isBusy && {
          '& .react-flow__node': {
            cursor: 'not-allowed !important',
          },
          '& .react-flow__node *': {
            cursor: 'not-allowed !important',
          },
        }),
      }}
      onContextMenu={onPaneContextMenu}
    >
      {/* Phase hint */}
      {phaseConfig && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: alpha(phaseConfig.color, 0.08),
            border: `1px solid ${alpha(phaseConfig.color, 0.2)}`,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: phaseConfig.color,
              animation: phase === 'executing' ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Typography variant="caption" fontWeight={600} sx={{ color: phaseConfig.color }}>
            {phaseConfig.label}
          </Typography>
          {phaseDetail && (
            <Typography variant="caption" color="text.secondary">
              {phaseDetail}
            </Typography>
          )}
        </Box>
      )}

      {/* Execution overlay */}
      {executionOverlay && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 3,
            width: 280,
            pointerEvents: 'auto',
          }}
        >
          {executionOverlay}
        </Box>
      )}

      {/* Empty state */}
      {pipelineNodes.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            zIndex: 2,
          }}
        >
          <BuildIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography variant="body1" color="text.secondary">
            No pipeline steps yet
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Chat with the agent to build your data pipeline
          </Typography>
        </Box>
      )}

      {/* FAB removed — use "+ Add Transform" toolbar button or Cmd+K instead */}

      <ReactFlow
        nodes={styledNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        onPaneClick={onPaneClick}
        panOnScroll
        zoomOnScroll
        zoomOnPinch
        zoomOnDoubleClick={false}
        nodesDraggable={true}
        nodesConnectable={false}
        fitViewOptions={{ padding: 0.2 }}
      >
        <MiniMap
          pannable
          zoomable
          style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
          nodeColor={(node) => {
            const data = node.data as DAGNodeData;
            if (data.status === 'completed') return '#22c55e';
            if (data.status === 'running') return '#3b82f6';
            if (data.status === 'failed') return '#ef4444';
            return '#94a3b8';
          }}
        />
        <Controls showInteractive={false}>
          <ControlButton
            onClick={() => applyLayout('TB')}
            title="Auto-layout: top → bottom"
            aria-label="Auto-layout top to bottom"
            style={{
              backgroundColor: layoutDirection === 'TB' ? '#dbeafe' : undefined,
              color: layoutDirection === 'TB' ? '#1d4ed8' : undefined,
            }}
          >
            <ArrowDownIcon sx={{ fontSize: 14 }} />
          </ControlButton>
          <ControlButton
            onClick={() => applyLayout('LR')}
            title="Auto-layout: left → right"
            aria-label="Auto-layout left to right"
            style={{
              backgroundColor: layoutDirection === 'LR' ? '#dbeafe' : undefined,
              color: layoutDirection === 'LR' ? '#1d4ed8' : undefined,
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </ControlButton>
        </Controls>
        <Background variant={BackgroundVariant.Dots} color="#cbd5e1" gap={20} size={1} />

        {/* Persistent Outputs button */}
        {onViewAllOutputs && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 5,
            }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={onViewAllOutputs}
              disabled={!outputsEnabled}
              sx={{
                fontSize: 11,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#2563eb',
                '&:hover': { bgcolor: '#1d4ed8' },
                borderRadius: 1.5,
                px: 1.5,
                py: 0.5,
                boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
                minWidth: 0,
              }}
            >
              Outputs{outputCount ? ` (${outputCount})` : ''}
            </Button>
          </Box>
        )}
      </ReactFlow>
    </Box>
  );
};

// ── Exported wrapper with ReactFlowProvider ──────────────────────────

const DAGCanvas: React.FC<DAGCanvasProps> = (props) => (
  <ReactFlowProvider>
    <DAGCanvasInner {...props} />
  </ReactFlowProvider>
);

export default DAGCanvas;
export type { DAGCanvasProps };
